import axios from '../axiosInstance';
import qs from 'qs';

/**
 * DataGrid-style query for enabled LA areas (keyset pagination, sort, filter).
 * Backend: POST api/LaArea/queryEnabled
 * Returns: { items, cursor, hasMore }
 */
export async function queryEnabledLaAreas({
  search,
  pageSize,
  cursor,
  sortBy,
  sortDir,
  filterModel,
  signal = null,
} = {}) {
  const body = {
    search: search ?? null,
    pageSize,
    cursor: cursor ?? null,
    sortBy: sortBy ?? 'name',
    sortDir: sortDir ?? 'asc',
    filterModel: filterModel ?? { items: [], logicOperator: 'and' },
  };

  const { data } = await axios.post('/LaArea/queryEnabled', body, { signal });
  return data;
}

/**
 * Fetch LA Areas with keyset pagination.
 * @param {Object} [params]
 * @param {string} [params.search='']
 * @param {number} [params.pageSize=50]
 * @param {number|null} [params.lastId=null]
 * @param {string[]|null} [params.searchFields=null] - optional address fields for autocomplete
 * @param {boolean} [params.searchAll=false] - when true, search all fields (advanced lookup)
 * @returns {Promise<{ items: Array<{id:number,name:string,code:string|null}>, lastId: number|null, hasMore: boolean }>}
 */
export async function getLaAreas({
    search = '',
    pageSize = 50,
    lastId = null,
    searchFields = null,
    searchAll = false,
    signal = null,
} = {}) {
    const params = { search, pageSize, lastId };
    if (searchAll) params.searchAll = true;
    if (Array.isArray(searchFields) && searchFields.length) {
        params.searchFields = searchFields;
    }
    const { data } = await axios.get('/LaArea/getLaAreas', {
        params,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'repeat', skipNulls: true }),
        signal,
    });
    return data;
}

/**
 * (Optional) Small adapter if any existing code still calls with a string.
 * Usage: fetchLaAreasString('term') -> same PageResult shape.
 */
export function fetchLaAreasString(search = '', signal = null) {
    return getLaAreas({ search, signal });
}

export async function getLaAreasEnabled({
  search = '',
  pageSize = 50,
  lastId = null,
  searchFields = null,
  searchAll = false,
  signal = null,
} = {}) {
  const params = { search, pageSize, lastId };
  if (searchAll) params.searchAll = true;
  if (Array.isArray(searchFields) && searchFields.length) {
    params.searchFields = searchFields;
  }
  const { data } = await axios.get('/LaArea/getEnabled', {
    params,
    paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'repeat', skipNulls: true }),
    signal,
  });
  return data;
}

export async function getLaAreaById(id, signal = null) {
  const { data } = await axios.get(`/LaArea/${id}`, { signal });
  return data;
}

export async function createLaArea(payload, signal = null) {
  const { data } = await axios.post('/LaArea', payload, { signal });
  return data;
}

export async function updateLaArea(id, payload, signal = null) {
  await axios.put(`/LaArea/${id}`, payload, { signal });
  return true;
}

export async function deleteLaArea(id, signal = null) {
  const { data } = await axios.delete(`/LaArea/${id}`, { signal });
  return data;
}

export async function restoreLaArea(id, signal = null) {
  await axios.post(`/LaArea/${id}/restore`, null, { signal });
  return true;
}

export async function getEnabledLaAreasByIds(ids, signal = null) {
  if (!ids || ids.length === 0) return { items: [] };
  const res = await axios.post(
    '/LaArea/byIdsEnabled',
    { filterModel: { items: [], logicOperator: 'and' } },
    {
      params: { ids },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      signal,
    }
  );
  return res.data;
}
